import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Bell, Smartphone, Mail, Phone, Clock, AlertTriangle, 
  Save, Shield, Zap 
} from 'lucide-react';
import { toast } from 'sonner';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { getCompanyId } from '../../utils/tenantContext';

interface NotificationChannel {
  enabled: boolean;
  quietHoursOverride: boolean; // If true, rings even during quiet hours
}

interface SeverityPolicy {
  sms: NotificationChannel;
  email: NotificationChannel;
  push: NotificationChannel;
  voice: NotificationChannel;
  autoEscalate: boolean;
  escalationDelayMin: number;
}

interface NotificationSettings {
  quietHoursStart: string; // "22:00"
  quietHoursEnd: string;   // "06:00"
  policies: {
    critical: SeverityPolicy;
    high: SeverityPolicy;
    medium: SeverityPolicy;
    low: SeverityPolicy;
  };
  dailyCapPerUser: number;
}

const DEFAULT_POLICY: SeverityPolicy = {
  sms: { enabled: false, quietHoursOverride: false },
  email: { enabled: true, quietHoursOverride: false },
  push: { enabled: true, quietHoursOverride: false },
  voice: { enabled: false, quietHoursOverride: false },
  autoEscalate: false,
  escalationDelayMin: 30,
};

const DEFAULT_SETTINGS: NotificationSettings = {
  quietHoursStart: '22:00',
  quietHoursEnd: '06:00',
  dailyCapPerUser: 50,
  policies: {
    critical: { 
      ...DEFAULT_POLICY, 
      sms: { enabled: true, quietHoursOverride: true },
      voice: { enabled: true, quietHoursOverride: true },
      push: { enabled: true, quietHoursOverride: true },
      autoEscalate: true,
      escalationDelayMin: 15
    },
    high: { ...DEFAULT_POLICY, sms: { enabled: true, quietHoursOverride: false } },
    medium: { ...DEFAULT_POLICY },
    low: { ...DEFAULT_POLICY, push: { enabled: false, quietHoursOverride: false } }
  }
};

export default function NotificationPolicyEditor({ isDarkMode = false }: { isDarkMode?: boolean }) {
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const companyId = getCompanyId();

  useEffect(() => {
    loadSettings();
  }, [companyId]);

  const loadSettings = async () => {
    if (!companyId) return;
    try {
      const docRef = doc(db, `companies/${companyId}/settings`, 'notifications');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        setSettings({ ...DEFAULT_SETTINGS, ...snap.data() as NotificationSettings });
      }
    } catch (error) {
      console.error('Failed to load notification settings', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const docRef = doc(db, `companies/${companyId}/settings`, 'notifications');
      await setDoc(docRef, settings);
      toast.success('Notification policies saved');
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePolicy = (severity: keyof NotificationSettings['policies'], field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      policies: {
        ...prev.policies,
        [severity]: {
          ...prev.policies[severity],
          [field]: value
        }
      }
    }));
  };

  const updateChannel = (severity: keyof NotificationSettings['policies'], channel: 'sms'|'email'|'push'|'voice', field: keyof NotificationChannel, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      policies: {
        ...prev.policies,
        [severity]: {
          ...prev.policies[severity],
          [channel]: {
            ...prev.policies[severity][channel],
            [field]: value
          }
        }
      }
    }));
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div></div>;
  }

  return (
    <div className={`max-w-5xl mx-auto p-6 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-rose-600" />
            Notification Matrix
          </h1>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Configure delivery channels, quiet hours, and escalation rules per severity level.
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-semibold shadow-lg transition-all disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Global Settings */}
      <div className={`mb-8 p-6 rounded-2xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Global Guardrails
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Quiet Hours Start</label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="time"
                value={settings.quietHoursStart}
                onChange={e => setSettings({...settings, quietHoursStart: e.target.value})}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Quiet Hours End</label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="time"
                value={settings.quietHoursEnd}
                onChange={e => setSettings({...settings, quietHoursEnd: e.target.value})}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Daily Cap (Per User)</label>
            <div className="relative">
              <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                value={settings.dailyCapPerUser}
                onChange={e => setSettings({...settings, dailyCapPerUser: parseInt(e.target.value) || 0})}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-gray-50 border-gray-300'}`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Severity Matrix */}
      <div className="space-y-6">
        {(['critical', 'high', 'medium', 'low'] as const).map((severity) => {
          const policy = settings.policies[severity];
          const color = 
            severity === 'critical' ? 'text-red-600 border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800' :
            severity === 'high' ? 'text-orange-600 border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800' :
            severity === 'medium' ? 'text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800' :
            'text-gray-600 border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700';

          return (
            <motion.div
              key={severity}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}
            >
              <div className={`px-6 py-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${color}`}>
                    {severity}
                  </span>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {severity === 'critical' ? 'Immediate action required (Pump failure, Leak)' :
                     severity === 'high' ? 'Urgent but not emergency (Pressure warning)' :
                     severity === 'medium' ? 'Standard operational alerts' : 'Information only'}
                  </span>
                </div>
                {severity === 'critical' && (
                  <div className="flex items-center gap-2 text-xs font-medium text-rose-600 bg-rose-100 dark:bg-rose-900/30 px-3 py-1 rounded-full">
                    <Zap className="w-3 h-3" />
                    Always overrides silent mode
                  </div>
                )}
              </div>

              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Channels */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">Delivery Channels</h3>
                  
                  {[
                    { id: 'push', label: 'Push Notification', icon: Smartphone },
                    { id: 'email', label: 'Email', icon: Mail },
                    { id: 'sms', label: 'SMS Text', icon: Smartphone },
                    { id: 'voice', label: 'Voice Call', icon: Phone },
                  ].map((ch) => {
                    const channelKey = ch.id as 'push'|'email'|'sms'|'voice';
                    const config = policy[channelKey];
                    
                    return (
                      <div key={ch.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-900/50">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${config.enabled ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30' : 'bg-gray-200 text-gray-500 dark:bg-slate-800'}`}>
                            <ch.icon className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-sm">{ch.label}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <span className="text-xs text-gray-500">Override Quiet Hours</span>
                            <input
                              type="checkbox"
                              checked={config.quietHoursOverride}
                              onChange={(e) => updateChannel(severity, channelKey, 'quietHoursOverride', e.target.checked)}
                              disabled={!config.enabled}
                              className="rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                            />
                          </label>
                          <button
                            onClick={() => updateChannel(severity, channelKey, 'enabled', !config.enabled)}
                            className={`
                              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                              ${config.enabled ? 'bg-rose-600' : 'bg-gray-300 dark:bg-slate-700'}
                            `}
                          >
                            <span className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${config.enabled ? 'translate-x-6' : 'translate-x-1'}
                            `} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Escalation Rules */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">Escalation Logic</h3>
                  
                  <div className={`p-4 rounded-xl border ${policy.autoEscalate ? 'border-rose-200 bg-rose-50 dark:bg-rose-900/10 dark:border-rose-800' : 'border-gray-200 bg-gray-50 dark:bg-slate-900/50 dark:border-slate-700'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${policy.autoEscalate ? 'text-rose-600' : 'text-gray-400'}`} />
                        <span className="font-medium text-sm">Auto-Escalation</span>
                      </div>
                      <button
                        onClick={() => updatePolicy(severity, 'autoEscalate', !policy.autoEscalate)}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                          ${policy.autoEscalate ? 'bg-rose-600' : 'bg-gray-300 dark:bg-slate-700'}
                        `}
                      >
                        <span className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${policy.autoEscalate ? 'translate-x-6' : 'translate-x-1'}
                        `} />
                      </button>
                    </div>

                    {policy.autoEscalate && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                            Trigger if not acknowledged within
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={policy.escalationDelayMin}
                              onChange={(e) => updatePolicy(severity, 'escalationDelayMin', parseInt(e.target.value))}
                              className={`w-20 px-3 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300'}`}
                            />
                            <span className="text-sm text-gray-500">minutes</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 italic">
                          System will automatically retry via Voice Call and notify the manager on duty.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
